/**
 * Trusted, pinned hydration runtime for live-data dashboards (Phase 2).
 *
 * This is the ONLY JavaScript that ever runs inside a micro-app iframe. The
 * iframe is opaque-origin (`sandbox="allow-scripts"`, no `allow-same-origin`),
 * so this script cannot reach the parent's auth/DOM. It also cannot fetch:
 * the injected CSP sets `connect-src 'none'`. Data arrives only via
 * origin-checked `postMessage` from the trusted parent.
 *
 * Responsibilities:
 *  - Listen for `{ type: "micro-app:hydrate", bindings: {...} }` messages.
 *  - Replace each `[data-binding="key"]` element's text with the live value
 *    (scalar bindings), or render a chart for `[data-chart][data-binding]`.
 *  - Leave the publish-time fallback content in place on error/no-data.
 *
 * The parent injects this via {@link injectHydrationRuntime} AFTER nh3
 * sanitization (same seam as brand injection on the backend), so the LLM can
 * never smuggle script through it.
 */

import { FRAPPE_CHARTS_UMD } from "./frappeChartsVendor";

/** Marker so we can find/verify the injected block and avoid double-injection. */
export const HYDRATION_MARKER = "data-micro-app-runtime";

/** Marker on the injected, pinned chart library script. */
export const CHART_LIB_MARKER = "data-micro-app-charts";

/**
 * Minimal base CSS for chart containers, injected into <head>. Frappe injects
 * its own SVG/tooltip styles at runtime; this just ensures the container has a
 * sensible box before the chart mounts and a readable font.
 */
const CHART_BASE_CSS =
  "<style>" +
  // The data-chart element is owned by the chart library: force it transparent
  // and borderless so a stray model-applied border/background doesn't draw a
  // second box inside the surrounding card (the card styling lives on an outer
  // wrapper). No padding so the SVG fills the full container.
  "[data-chart]{display:block;width:100%;min-height:0;background:transparent!important;" +
  "border:0!important;box-shadow:none!important;padding:0!important}" +
  ".chart-container,.frappe-chart{font-family:inherit}" +
  // The chart container must establish a positioning context so frappe's
  // absolutely-positioned tooltip anchors to the chart (not a distant ancestor),
  // which fixes the mis-placed hover tip inside our nested card/iframe layout.
  ".chart-container{position:relative!important}" +
  // Restyle frappe's tooltip (.graph-svg-tip) to be clean + on-brand: a light
  // card with a subtle border/shadow instead of the default dark box.
  ".graph-svg-tip{" +
  "background:var(--brand-surface,#ffffff)!important;" +
  "color:var(--brand-text,#1f2937)!important;" +
  "border:1px solid rgba(0,0,0,0.08)!important;" +
  "border-radius:8px!important;" +
  "box-shadow:0 4px 14px rgba(0,0,0,0.12)!important;" +
  "padding:8px 10px!important;" +
  "font-size:12px!important;text-align:left!important" +
  "}" +
  ".graph-svg-tip .title{" +
  "color:var(--brand-text-muted,#6b7280)!important;" +
  "font-weight:600!important;font-size:11px!important;" +
  "text-transform:uppercase!important;letter-spacing:0.03em!important;" +
  "padding:0 0 2px!important;margin:0!important;display:block!important" +
  "}" +
  ".graph-svg-tip .value,.graph-svg-tip strong{" +
  "color:var(--brand-text,#111827)!important;font-weight:700!important" +
  "}" +
  ".graph-svg-tip ul,.graph-svg-tip ol{margin:0!important;padding:0!important}" +
  ".graph-svg-tip li{padding:0!important}" +
  // Recolor the little pointer triangle to match the light card.
  ".graph-svg-tip .svg-pointer{border-top-color:var(--brand-surface,#ffffff)!important}" +
  "</style>";

/** Reserved for any post-lib CSS tweaks (kept separate for clarity). */
const CHART_CSS = "";

/**
 * The runtime body, kept as a plain string so it can be injected as trusted
 * inline script. It is intentionally dependency-free and self-contained.
 *
 * Exported (as {@link RUNTIME_BODY}) so tests can execute it in jsdom and assert
 * the hydration DOM behaviour without a real iframe.
 */
export const RUNTIME_BODY = `
(function () {
  "use strict";
  function setText(el, text) {
    // textContent only — never innerHTML — so values can't inject markup.
    el.textContent = text;
  }
  // Latest hydrated value per binding key — charts read this on (re)render.
  var latestBindings = {};
  function applyBinding(key, payload) {
    latestBindings[key] = payload || null;
    var nodes = document.querySelectorAll('[data-binding="' + CSS.escape(key) + '"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      // Chart containers are hydrated by renderCharts(), not as text cells.
      if (el.hasAttribute("data-chart")) continue;
      // Clear any prior-refresh state markers so stale flags don't linger.
      el.removeAttribute("data-binding-error");
      el.removeAttribute("data-binding-stale");
      el.removeAttribute("data-binding-structured");
      if (el.title === "Couldn't refresh — showing last known value") el.removeAttribute("title");
      if (!payload) continue;
      if (payload.error) {
        // Keep the last-known value visible but flag it as stale so the cell
        // (and the parent badge) can show a "couldn't refresh" state.
        el.setAttribute("data-binding-error", String(payload.error));
        el.title = "Couldn't refresh — showing last known value";
        var lastKnown = payload.value;
        if (lastKnown !== null && lastKnown !== undefined && typeof lastKnown !== "object") {
          setText(el, String(lastKnown));
        }
        el.setAttribute("data-binding-stale", "1");
        continue;
      }
      var value = payload.value;
      if (value === null || value === undefined) {
        // A genuine empty result (e.g. a KPI over zero rows). Render an em-dash
        // so it's clearly "no data", not a real zero and not un-hydrated.
        setText(el, "\u2014");
        el.setAttribute("data-binding-hydrated", "1");
        continue;
      }
      if (typeof value === "object") {
        // Structured payload (series/pivot/raw rows) with no scalar to show.
        // Charts will consume these later; for now surface a neutral marker
        // instead of leaving the misleading publish-time placeholder.
        var count = Array.isArray(value)
          ? value.length
          : (value && Array.isArray(value.x) ? value.x.length : null);
        setText(el, count !== null ? "\u2014" : "\u2014");
        el.setAttribute("data-binding-structured", "1");
        el.setAttribute("data-binding-hydrated", "1");
        continue;
      }
      setText(el, String(value));
      el.setAttribute("data-binding-hydrated", "1");
    }
  }

  // ---- Charts ------------------------------------------------------------
  // The LLM emits inert declarative chart specs (data-chart + data-binding +
  // data-x/y/title). Our trusted, injected frappe-charts runtime reads the spec
  // + the hydrated series/pivot data and renders a real SVG chart. The library
  // is only present when the document actually contains a chart (see
  // injectHydrationRuntime), so guard on it.
  var CHART_TYPES = { bar: "bar", line: "line", area: "line", pie: "pie", donut: "percentage", percentage: "percentage" };
  var chartInstances = {};

  function brandColors() {
    // Pull brand palette from the doc so charts match the dashboard. Falls back
    // to a neutral set the runtime ships with.
    var cs = getComputedStyle(document.documentElement);
    var vars = ["--brand-primary", "--brand-secondary", "--brand-accent-1", "--brand-accent-2", "--brand-accent-3"];
    var out = [];
    for (var i = 0; i < vars.length; i++) {
      var v = cs.getPropertyValue(vars[i]).trim();
      if (v) out.push(v);
    }
    return out.length ? out : ["#5e64ff", "#743ee2", "#ff5858", "#ffa00a", "#28a745"];
  }

  function toChartData(value, spec) {
    // series transform -> {x:[], y:[]}; pivot transform -> {label: value}.
    // Guard against non-numeric values: Number() coerces garbage to NaN, which
    // frappe draws as a broken plot instead of falling back cleanly.
    if (value && Array.isArray(value.x) && Array.isArray(value.y)) {
      var ys = value.y.map(Number);
      if (!ys.every(Number.isFinite)) return null;
      return { labels: value.x.map(String), datasets: [{ name: spec.yName || "", values: ys }] };
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      var labels = [], values = [];
      for (var k in value) {
        if (Object.prototype.hasOwnProperty.call(value, k)) { labels.push(String(k)); values.push(Number(value[k])); }
      }
      if (!values.every(Number.isFinite)) return null;
      if (labels.length) return { labels: labels, datasets: [{ name: spec.yName || "", values: values }] };
    }
    return null;
  }

  function renderOneChart(el) {
    if (!window.frappe || !window.frappe.Chart) return;
    var rawType = (el.getAttribute("data-chart") || "").toLowerCase();
    var type = CHART_TYPES[rawType];
    var key = el.getAttribute("data-binding");
    if (!type || !key) return; // unknown chart type / no binding -> keep fallback
    var payload = latestBindings[key];
    if (!payload || payload.error) {
      // No data or an error. If the chart already rendered once, keep that good
      // chart visible (a transient refresh error must never blank it). If it has
      // NEVER rendered (e.g. a preview whose bindings failed to load on first
      // paint), collapse the empty container so it doesn't sit as a blank
      // bordered plot next to populated KPIs.
      if (payload && payload.error) el.setAttribute("data-chart-stale", "1");
      if (!chartInstances[key]) {
        el.style.display = "none";
        el.setAttribute("data-chart-collapsed", "1");
      }
      return;
    }
    // Have data: un-collapse if a prior failed load hid the empty container.
    if (el.getAttribute("data-chart-collapsed")) {
      el.style.display = "";
      el.removeAttribute("data-chart-collapsed");
    }
    var spec = {
      title: el.getAttribute("data-title") || "",
      yName: el.getAttribute("data-y") || "",
    };
    var data = toChartData(payload.value, spec);
    if (!data) return; // structured data we can't chart -> keep fallback

    el.removeAttribute("data-chart-stale");
    // Recreate on each refresh: frappe has no uniform in-place update across
    // types, and recreation is cheap for dashboard-sized data.
    if (chartInstances[key] && chartInstances[key].destroy) {
      try { chartInstances[key].destroy(); } catch (e) {}
    }
    el.textContent = ""; // clear the pre-hydration fallback before mounting
    try {
      var cfg = {
        data: data,
        type: type,
        height: Number(el.getAttribute("data-height")) || 240,
        colors: brandColors(),
        animate: true,
        axisOptions: { xIsSeries: type === "line" },
      };
      // "area" is a line chart with the region filled — distinguishes it visually.
      if (rawType === "area") cfg.lineOptions = { regionFill: 1, hideDots: 0 };
      chartInstances[key] = new window.frappe.Chart(el, cfg);
      el.setAttribute("data-chart-rendered", "1");
    } catch (e) {
      // Rendering failed: restore a minimal textual fallback so it's not blank.
      el.setAttribute("data-chart-error", "1");
    }
  }

  function renderCharts() {
    var nodes = document.querySelectorAll("[data-chart][data-binding]");
    for (var i = 0; i < nodes.length; i++) {
      try { renderOneChart(nodes[i]); } catch (e) { /* isolate per-chart */ }
    }
    // Nudge frappe to re-measure the container after layout settles. On first
    // mount inside our nested flex/iframe layout the SVG can be measured before
    // final width is known, which scales the viewBox and throws off the tooltip
    // coordinate math. Frappe recomputes width + redraws on window "resize", so
    // firing one realigns the charts and their tooltips.
    if (nodes.length) {
      setTimeout(function () {
        try { window.dispatchEvent(new Event("resize")); } catch (e) {}
      }, 60);
    }
  }

  var hydrated = false;
  window.addEventListener("message", function (event) {
    var data = event.data;
    if (!data || data.type !== "micro-app:hydrate" || !data.bindings) return;
    hydrated = true;
    var bindings = data.bindings;
    for (var key in bindings) {
      if (Object.prototype.hasOwnProperty.call(bindings, key)) {
        try { applyBinding(key, bindings[key]); } catch (e) { /* isolate per-binding */ }
      }
    }
    try { renderCharts(); } catch (e) { /* charts never block hydration */ }
    // Tell the parent hydration finished (for the "last updated" badge timing).
    if (window.parent) window.parent.postMessage({ type: "micro-app:hydrated" }, "*");
  });
  // Announce readiness so the parent knows when to post data. Because the parent's
  // message listener may not be attached yet when this first fires, keep
  // re-announcing until we actually receive data (or give up after ~5s). This
  // closes the race where the very first "ready" is missed.
  function announce() {
    if (hydrated || !window.parent) return;
    window.parent.postMessage({ type: "micro-app:ready" }, "*");
  }
  announce();
  var attempts = 0;
  var readyTimer = setInterval(function () {
    attempts++;
    if (hydrated || attempts > 25) { clearInterval(readyTimer); return; }
    announce();
  }, 200);
})();
`;

/**
 * Restrictive CSP for the generated document: no network, no external scripts.
 * `connect-src 'none'` is the key line — even if a rogue script slipped past
 * nh3, it could not beacon data out. Inline script is allowed via
 * `'unsafe-inline'` because our trusted runtime is inline; the iframe's opaque
 * origin + no `allow-same-origin` is the real containment boundary.
 */
const CSP_META =
  `<meta http-equiv="Content-Security-Policy" ` +
  `content="default-src 'self' data: blob:; ` +
  `img-src data: blob:; ` +
  `style-src 'unsafe-inline'; ` +
  `font-src data:; ` +
  `script-src 'unsafe-inline'; ` +
  `connect-src 'none'">`;

/**
 * Strip the document-structure tags nh3 leaves behind (it sanitizes body
 * *content* and drops `<!DOCTYPE>`, `<html>`, `<head>`, `<body>`). We rebuild a
 * clean skeleton around the fragment so the injected CSP lands in a real
 * `<head>` — otherwise browsers mis-scope a `<meta http-equiv>` that appears in
 * body position, which can leak the policy to the parent document.
 */
function stripDocumentTags(html: string): string {
  return html
    .replace(/<!DOCTYPE[^>]*>/gi, "")
    .replace(/<\/?(?:html|head|body)\b[^>]*>/gi, "")
    .trim();
}

/**
 * Wrap a sanitized dashboard fragment in a well-formed HTML document that
 * carries the trusted hydration runtime and a scoped CSP.
 *
 * The CSP MUST live inside a proper `<head>`; a fragment starting with a bare
 * `<meta http-equiv="Content-Security-Policy">` is parsed in body position and
 * the browser may apply it to the embedding page, breaking the parent app.
 *
 * Idempotent: if the runtime marker is already present the HTML is returned
 * unchanged.
 */
export function injectHydrationRuntime(html: string): string {
  if (html.includes(HYDRATION_MARKER)) return html;

  const fragment = stripDocumentTags(html);

  // Dev-time tripwire: an unescaped "</script>" inside either injected script
  // body would silently truncate the script and break every dashboard. Fail
  // loudly at injection time instead.
  if (RUNTIME_BODY.includes("</script") || FRAPPE_CHARTS_UMD.includes("</script")) {
    throw new Error("hydrationRuntime: injected script body contains '</script' — escape it before embedding");
  }

  const scriptBlock = `<script ${HYDRATION_MARKER}>${RUNTIME_BODY}</script>`;

  // Only ship the (larger) chart library when the dashboard actually uses a
  // chart. The runtime's chart adapter no-ops when window.frappe is absent.
  const needsCharts = /\bdata-chart\s*=/.test(fragment);
  const chartLib = needsCharts ? `<script ${CHART_LIB_MARKER}>${FRAPPE_CHARTS_UMD}</script>${CHART_CSS}` : "";

  return (
    "<!DOCTYPE html><html><head>" +
    CSP_META +
    '<meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1.0">' +
    CHART_BASE_CSS +
    "</head><body>" +
    fragment +
    chartLib +
    scriptBlock +
    "</body></html>"
  );
}

/**
 * Parse the bindings manifest embedded in the dashboard HTML.
 * Mirrors the backend `bindings_manifest` block. Returns the list of
 * binding keys the viewer must hydrate.
 */
export function extractBindingKeys(html: string): string[] {
  const BINDINGS_RE = /<script[^>]*\bdata-micro-app-bindings\b[^>]*>([\s\S]*?)<\/script>/i;
  const match = BINDINGS_RE.exec(html);
  if (!match) return [];
  try {
    const parsed = JSON.parse(match[1]);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((b) => (b && typeof b.binding_key === "string" ? b.binding_key : null))
      .filter((k): k is string => k !== null);
  } catch {
    return [];
  }
}
